import { useMemo } from "react";
import { Trophy } from "@phosphor-icons/react";

/**
 * Computes per-player point totals across all scoreboards of a single court.
 * Each player in Pair A gets score_a, each player in Pair B gets score_b, summed over scoreboards.
 * ALSO adds points from the planilla (per cancha) if present.
 * Shows top-N ranking ordered desc by total points.
 */
export default function CourtRanking({ scoreboards, planilla, courtId, testIdPrefix }) {
  const ranking = useMemo(() => {
    const map = new Map(); // key (user_id or name) → { name, total, retas, wins }
    (scoreboards || []).forEach((sb) => {
      const lineup = sb.lineup || {};
      const sa = Number(sb.score_a) || 0;
      const sb_score = Number(sb.score_b) || 0;
      const winA = sa > sb_score;
      const winB = sb_score > sa;
      const addPts = (slot, points, didWin) => {
        const p = lineup[slot];
        if (!p) return;
        const key = p.user_id || p.name;
        if (!key) return;
        const entry = map.get(key) || { key, name: p.name, is_guest: !!p.is_guest, total: 0, retas: 0, wins: 0, planilla: 0 };
        entry.total += points;
        entry.retas += 1;
        if (didWin) entry.wins += 1;
        map.set(key, entry);
      };
      addPts("a1", sa, winA);
      addPts("a2", sa, winA);
      addPts("b1", sb_score, winB);
      addPts("b2", sb_score, winB);
    });
    // Merge planilla totals
    (planilla?.players || []).forEach((p) => {
      const key = p.user_id || p.name;
      if (!key || (!p.name && !p.user_id)) return;
      const t = Number(p.total) || 0;
      if (t <= 0) return;
      const entry = map.get(key) || { key, name: p.name, is_guest: !!p.is_guest, total: 0, retas: 0, wins: 0, planilla: 0 };
      entry.total += t;
      entry.planilla += t;
      map.set(key, entry);
    });
    return Array.from(map.values()).sort((a, b) => b.total - a.total || b.wins - a.wins);
  }, [scoreboards, planilla]);

  if (!ranking.length) return null;

  const totalRounds = new Set((scoreboards || []).map((s) => s.round || 1)).size;
  const hasPlanilla = (planilla?.players || []).some((p) => Number(p.total) > 0);

  return (
    <div className="mt-3 p-3 bg-[#0A0A0A] border border-[#CCFF00]/15 rounded-md" data-testid={`${testIdPrefix}-ranking`}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Trophy size={14} weight="fill" className="text-[#CCFF00]" />
        <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#CCFF00]">Ranking de la cancha</span>
        <span className="text-[10px] text-gray-500 ml-auto">{totalRounds} ronda{totalRounds !== 1 ? "s" : ""} · {scoreboards.length} pizarron{scoreboards.length !== 1 ? "es" : ""}{hasPlanilla ? " · planilla" : ""}</span>
      </div>
      <div className="space-y-1">
        {ranking.slice(0, 8).map((p, i) => {
          const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : null;
          const posBg = i === 0 ? "bg-amber-400/15 border-amber-400/40" : i === 1 ? "bg-gray-400/15 border-gray-400/40" : i === 2 ? "bg-orange-700/15 border-orange-700/40" : "bg-white/[0.03] border-white/10";
          return (
            <div key={p.key} className={`flex items-center justify-between px-2 py-1.5 border rounded ${posBg}`} data-testid={`${testIdPrefix}-ranking-row-${i}`}>
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xs font-mono text-gray-400 w-5 text-center shrink-0">{medal || `#${i + 1}`}</span>
                <span className="text-xs text-white truncate" title={p.name}>{p.name}{p.is_guest ? " (inv)" : ""}</span>
                {p.planilla > 0 && <span className="text-[9px] text-amber-300/70 font-mono shrink-0" title="Puntos de planilla">+{p.planilla}P</span>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[9px] text-gray-500 uppercase">{p.wins}V · {p.retas}j</span>
                <span className="font-['Barlow_Condensed'] text-lg font-black text-[#CCFF00] leading-none" data-testid={`${testIdPrefix}-ranking-pts-${i}`}>{p.total}</span>
                <span className="text-[9px] text-gray-500 uppercase">pts</span>
              </div>
            </div>
          );
        })}
        {ranking.length > 8 && (
          <div className="text-[9px] text-gray-500 italic text-center pt-1">y {ranking.length - 8} más</div>
        )}
      </div>
    </div>
  );
}
